/**
 * Component: EventPaymentsTab
 * Payments ledger for the event: every payment and reversal, totals by method,
 * receipts (issue / view), CSV export.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Download, FileText, Printer, Receipt as ReceiptIcon, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/hooks/usePermissions';
import { formatPrice } from '@/models/event';
import {
  getEventPayments,
  PAYMENT_METHOD_LABELS,
  SELECTABLE_PAYMENT_METHODS,
  normalizePaymentMethod,
  type EventPayment,
  type EventPaymentMethod,
} from '@/Services/eventPayments.service';
import { getReceiptById } from '@/features/receipts/data/receiptsRepository';
import { formatReceiptNumber, receiptToPrintData } from '@/features/receipts/types';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { ReceiptPrintPreview } from '@/features/receipts/components/ReceiptPrintPreview';
import { eventReceiptShare } from '../utils/receiptShare';
import type { Receipt } from '@/features/receipts/types';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { IssueEventReceiptPrompt } from './IssueEventReceiptPrompt';
import { csvFileName, downloadCsv } from '../utils/csv';
import { localDateStr, paymentsInRange, summarizePayments } from '../utils/dailyClose';
import { DailyCloseSheet } from '../print/DailyCloseSheet';
import { PrintPortal } from '../print/PrintPortal';

interface EventPaymentsTabProps {
  eventId: string;
  eventName: string;
}

function payerName(p: EventPayment) {
  return p.participant?.minister?.full_name ?? p.participant?.guest?.full_name ?? '—';
}

function payerCi(p: EventPayment) {
  return p.participant?.minister?.dni ?? p.participant?.guest?.dni ?? '';
}

export function EventPaymentsTab({ eventId, eventName }: EventPaymentsTabProps) {
  const { can } = usePermissions();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'RECORDED' | 'REVERSED'>('RECORDED');
  const [method, setMethod] = useState<'ALL' | EventPaymentMethod>('ALL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [closeDate, setCloseDate] = useState<string | null>(null);
  const [, setSearchParams] = useSearchParams();
  const [receiptFor, setReceiptFor] = useState<EventPayment | null>(null);
  const [preview, setPreview] = useState<Receipt | null>(null);

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['event_payments', eventId],
    queryFn: () => getEventPayments(eventId),
    enabled: can('event_payment.view'),
  });

  // Period filter first: the KPI cards and the per-recorder totals follow the date range
  const inRange = useMemo(() => paymentsInRange(payments, from || undefined, to || undefined), [payments, from, to]);
  const summary = useMemo(() => summarizePayments(inRange), [inRange]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inRange.filter(
      (p) =>
        (status === 'ALL' || p.status === status) &&
        (method === 'ALL' || normalizePaymentMethod(p.method) === method) &&
        (!q || payerName(p).toLowerCase().includes(q) || payerCi(p).toLowerCase().includes(q)),
    );
  }, [inRange, search, status, method]);

  const totals = useMemo(
    () => ({
      reversed: inRange.filter((p) => p.status === 'REVERSED').length,
      withoutReceipt: inRange.filter((p) => p.status === 'RECORDED' && !p.receipt_id).length,
    }),
    [inRange],
  );

  const today = localDateStr(new Date());
  const closePayments = useMemo(
    () => (closeDate ? payments.filter((p) => localDateStr(p.paid_at) === closeDate) : []),
    [payments, closeDate],
  );

  // Jump to the participant in the Participantes tab (its filters are read from ?pf= on mount)
  const goToParticipant = (name: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'participants');
      next.set('pf', JSON.stringify({ search: name }));
      return next;
    });

  const handleExport = () =>
    downloadCsv(
      csvFileName('pagos', eventName),
      ['Fecha', 'Participante', 'CI', 'Monto', 'Método', 'Estado', 'Recibo', 'Registrado por', 'Notas', 'Motivo reversión'],
      filtered.map((p) => [
        new Date(p.paid_at).toLocaleString('es-BO'),
        payerName(p),
        payerCi(p),
        Number(p.amount).toFixed(2),
        p.method ? PAYMENT_METHOD_LABELS[p.method] : '',
        p.status === 'RECORDED' ? 'Registrado' : 'Revertido',
        p.receipt ? formatReceiptNumber(p.receipt.number) : '',
        p.recorder?.full_name ?? '',
        p.notes ?? '',
        p.reversal_reason ?? '',
      ]),
    );

  const openReceipt = async (id: string) => setPreview(await getReceiptById(id));

  if (!can('event_payment.view')) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">Sin permiso para ver pagos.</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total recaudado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatPrice(summary.total)}</p>
            <p className="text-xs text-muted-foreground">{summary.count} pagos{from || to ? ' en el período' : ''}</p>
          </CardContent>
        </Card>
        {SELECTABLE_PAYMENT_METHODS.map((m) => (
          <Card key={m}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{PAYMENT_METHOD_LABELS[m]}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatPrice(summary.byMethod.get(m)?.total ?? 0)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {summary.byRecorder.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recaudado por usuario{from || to ? ' (período)' : ''}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {summary.byRecorder.map((r) => (
                <div key={r.name}>
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatPrice(r.total)} · {r.count} pago(s)
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Pagos registrados</CardTitle>
            <p className="text-sm text-muted-foreground">
              {totals.withoutReceipt > 0 && `${totals.withoutReceipt} sin recibo · `}
              {totals.reversed} revertidos
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setCloseDate(to || from || today)}>
              <Printer className="mr-2 size-4" /> Cierre del día
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={filtered.length === 0}>
              <Download className="mr-2 size-4" /> Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o CI"
                className="pl-9"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Desde</Label>
                <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Hasta</Label>
                <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="w-[150px]" />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFrom(today);
                  setTo(today);
                }}
              >
                Hoy
              </Button>
              {(from || to) && (
                <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); }}>
                  Todo el período
                </Button>
              )}
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RECORDED">Registrados</SelectItem>
                <SelectItem value="REVERSED">Revertidos</SelectItem>
                <SelectItem value="ALL">Todos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={method} onValueChange={(v) => setMethod(v as typeof method)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los métodos</SelectItem>
                {SELECTABLE_PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Participante</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Recibo</TableHead>
                  <TableHead>Registrado por</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}
                {!isLoading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      Sin pagos para los filtros seleccionados.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((p) => (
                  <TableRow key={p.id} className={p.status === 'REVERSED' ? 'opacity-60' : undefined}>
                    <TableCell className="whitespace-nowrap text-xs">{new Date(p.paid_at).toLocaleString('es-BO')}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="text-left font-medium hover:underline"
                        title="Ver en Participantes"
                        onClick={() => goToParticipant(payerName(p))}
                      >
                        {payerName(p)}
                      </button>
                      {p.notes && <div className="max-w-[240px] truncate text-xs text-muted-foreground">{p.notes}</div>}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatPrice(Number(p.amount))}</TableCell>
                    <TableCell>{p.method ? PAYMENT_METHOD_LABELS[p.method] : '—'}</TableCell>
                    <TableCell>
                      {p.status === 'RECORDED' ? (
                        <Badge variant="secondary">Registrado</Badge>
                      ) : (
                        <div className="space-y-0.5">
                          <Badge variant="destructive">Revertido</Badge>
                          {p.reversal_reason && (
                            <p className="max-w-[200px] text-xs text-muted-foreground">{p.reversal_reason}</p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.receipt ? (
                        <Button variant="link" className="h-auto p-0" onClick={() => openReceipt(p.receipt!.id)}>
                          <FileText className="mr-1 size-4" />
                          <span title={`Año ${p.receipt.year}`}>N° {formatReceiptNumber(p.receipt.number)}</span>
                          {p.receipt.status !== 'issued' && ' (anulado)'}
                        </Button>
                      ) : p.status === 'RECORDED' && can('event_payment.record') ? (
                        <Button variant="outline" size="sm" onClick={() => setReceiptFor(p)}>
                          <ReceiptIcon className="mr-1 size-4" /> Emitir
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.recorder?.full_name ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {receiptFor && (
        <IssueEventReceiptPrompt
          eventName={eventName}
          paymentId={receiptFor.id}
          participant={
            {
              minister_id: receiptFor.participant?.minister_id ?? null,
              minister: receiptFor.participant?.minister ?? null,
              guest: receiptFor.participant?.guest ?? null,
            } as unknown as ParticipantWithDetails
          }
          amount={Number(receiptFor.amount)}
          method={receiptFor.method ?? 'cash'}
          skipPrompt
          onClose={() => setReceiptFor(null)}
        />
      )}

      {closeDate && (
        <PrintPortal
          title="Cierre de caja"
          pageSize="letter"
          onClose={() => setCloseDate(null)}
          toolbar={
            <Input
              type="date"
              value={closeDate}
              max={today}
              onChange={(e) => e.target.value && setCloseDate(e.target.value)}
              className="w-[160px]"
              aria-label="Día del cierre"
            />
          }
        >
          <DailyCloseSheet eventName={eventName} date={closeDate} payments={closePayments} />
        </PrintPortal>
      )}

      {preview && (
        <ReceiptPrintPreview
          data={receiptToPrintData(preview, publicReceiptUrl(preview.public_token))}
          share={(() => {
            const owner = payments?.find((p) => p.receipt_id === preview.id)?.participant;
            if (!owner) return undefined;
            const phone =
              (owner.registration_data as { cell_phone?: string } | null)?.cell_phone ||
              owner.minister?.cell_phone ||
              owner.guest?.phone ||
              null;
            return eventReceiptShare(owner, phone, preview.number);
          })()}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
